import { useEffect } from "react";

const SESSION_KEY = "swiftcash:apply:exitIntent:fired";

/**
 * Fires `onExitIntent` when the user moves the cursor toward the top edge
 * of the viewport (a strong signal they're about to close the tab or hop
 * to another one). Desktop only — mobile has no mouseleave semantics.
 *
 * Fires at most once per browser session.
 */
export function useExitIntent({ enabled = true, onExitIntent, minTopThreshold = 4 }) {
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;

    // Skip on touch / mobile devices — no hover semantics.
    const isTouch = window.matchMedia("(hover: none)").matches
      || window.matchMedia("(max-width: 768px)").matches;
    if (isTouch) return;

    // Only once per session
    if (sessionStorage.getItem(SESSION_KEY) === "1") return;

    let armed = false;
    const armTimer = setTimeout(() => { armed = true; }, 2500); // give the page 2.5s before arming

    const handler = (e) => {
      if (!armed) return;
      // Any move from within the viewport toward the very top edge
      const toTop = e.clientY <= minTopThreshold;
      const outside = !e.relatedTarget && !e.toElement;
      if (toTop && outside) {
        sessionStorage.setItem(SESSION_KEY, "1");
        onExitIntent?.();
      }
    };
    document.addEventListener("mouseleave", handler);
    document.addEventListener("mouseout", handler);
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener("mouseleave", handler);
      document.removeEventListener("mouseout", handler);
    };
  }, [enabled, onExitIntent, minTopThreshold]);
}

export function resetExitIntentSession() {
  if (typeof window !== "undefined") sessionStorage.removeItem(SESSION_KEY);
}
